import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { useEffect, useState } from "react";
import { z } from "zod";

import { ppost } from "@/api";
import { Models } from "@/api/types";
import { Image, Text } from "@/atoms";
import { Button } from "@/atoms/button/Button";
import { IconButton } from "@/atoms/icon-button/IconButton";
import { Modal } from "@/atoms/modal/Modal";
import { PhoneNumberField, SimpleFormProvider, TextField } from "@/form";
import { removeNonDigits } from "@/lib/utils";
import Close from "atoms/icon/icons/close.svg";

type GiftCardShareModalProps = {
  open: boolean;
  onClose: () => void;
  giftCards: Models<"DisplayGiftCardListing">[];
  selectedGiftCard: {
    listingId: string;
    itemId: string;
  };
  platformLogo: string;
  locationId: string;
};

export const GiftCardShareModal = ({
  open,
  onClose,
  giftCards,
  selectedGiftCard,
  platformLogo,
  locationId,
}: GiftCardShareModalProps) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keyboardIsOpen, setKeyboardIsOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [formData, setFormData] = useState<{ firstName?: string } | null>(null);

  // Reset success state when modal is closed
  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
      setIsSubmitting(false);
      setFormData(null);
    }
  }, [open]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const keyboardWillShowListener = Keyboard.addListener(
        "keyboardWillShow",
        (info) => {
          setKeyboardIsOpen(true);
          setKeyboardHeight(info.keyboardHeight);
        }
      );

      const keyboardWillHideListener = Keyboard.addListener(
        "keyboardWillHide",
        () => {
          setKeyboardIsOpen(false);
          setKeyboardHeight(0);
        }
      );

      return () => {
        keyboardWillShowListener.then((listener) => listener.remove());
        keyboardWillHideListener.then((listener) => listener.remove());
      };
    }
  }, []);

  const selectedGiftCardImage =
    giftCards?.find((d) => d.id === selectedGiftCard.listingId)?.imageUri ||
    giftCards[0]?.imageUri;

  // Create a proper fallback image using the first gift card's base URL
  const defaultGiftCardImage = giftCards[0]?.imageUri
    ? giftCards[0].imageUri.substring(
        0,
        giftCards[0].imageUri.lastIndexOf("/") + 1
      ) + "giftcard-justforyou.webp"
    : "";

  const selectedGiftCardValue =
    giftCards
      ?.find((d) => d.id === selectedGiftCard.listingId)
      ?.giftCards.find((d) => d.packageId === selectedGiftCard.itemId)
      ?.baseCostInCents || 0;

  const handleFormSubmit = async (data: ShareGiftCardData) => {
    setIsSubmitting(true);
    setFormData({ firstName: data.firstName });
    return shareGiftCard({
      locationId,
      giftCardListingId: selectedGiftCard.listingId,
      giftCardItemId: selectedGiftCard.itemId,
      intendedBuyerFirstName: data.firstName,
      intendedBuyerLastName: data.lastName,
      intendedBuyerPhone: `+1${removeNonDigits(data.phoneNumber)}`,
    });
  };

  const handleSuccess = () => {
    setTimeout(() => {
      setShowSuccess(true);
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <Modal open={open} onClose={onClose} size="h-auto">
      <div
        className="relative px-6 pb-6 pt-3.5 transition-all duration-300 ease-in-out"
        style={{
          paddingBottom: keyboardIsOpen
            ? Capacitor.isNativePlatform()
              ? `${keyboardHeight + 24}px`
              : "120px"
            : "24px",
        }}
      >
        {/* Close button positioned absolutely in top-right */}
        <div className="absolute right-2 top-2 z-10">
          <IconButton
            svg={Close}
            size="small"
            style="iconOnly"
            onClick={onClose}
          />
        </div>

        {/* Title and helper text centered */}
        <div className="mb-6 text-center">
          <h2 className="font-serif text-h2 text-primary">
            {showSuccess ? "Sent!" : "Give someone a hint!"}
          </h2>
          {!showSuccess ? (
            <p className="text-body-2 mx-auto mt-2 max-w-sm text-primary">
              Share this link with friends and family to let them know what you
              really want.
            </p>
          ) : (
            <p className="text-body-2 mx-auto mt-2 max-w-sm text-primary">
              We sent your gift to {formData?.firstName || "them"}.
            </p>
          )}
        </div>

        {/* Gift Card Preview - always visible */}
        <div className="mb-6 flex justify-center">
          <div
            className="relative overflow-hidden shadow-lg"
            style={{ width: "254px", height: "144px" }}
          >
            <Image
              src={selectedGiftCardImage || defaultGiftCardImage}
              alt="Gift Card"
              className="h-full w-full rounded-lg object-cover"
            />
            <div className="absolute bottom-2 left-2 rounded px-2 py-1 text-white">
              <div className="flex items-start">
                <span className="mt-[0.2rem] text-xs font-bold">$</span>
                <span className="text-h2 font-bold">
                  {(selectedGiftCardValue / 100).toFixed(0)}
                </span>
              </div>
            </div>
            <div className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-white p-0.5">
              <Image
                src={platformLogo}
                alt="Platform logo"
                className="h-full w-full rounded-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Form or Success - simple show/hide */}
        {!showSuccess ? (
          <SimpleFormProvider
            schema={shareGiftCardSchema}
            onSubmit={handleFormSubmit}
            onSuccess={handleSuccess}
            defaultValues={{
              firstName: "",
              lastName: "",
              phoneNumber: "",
            }}
            hideLoading={isSubmitting}
          >
            <div className="mb-6 flex flex-col gap-4">
              <div className="flex flex-1 gap-4">
                <TextField
                  label="First Name"
                  name="firstName"
                  placeholder="First name"
                  hideValidation
                  hideLabel
                  autoFocus
                />
                <TextField
                  label="Last Name"
                  name="lastName"
                  placeholder="Last name"
                  hideValidation
                  hideLabel
                />
              </div>
              <div className="flex-1">
                <PhoneNumberField
                  label="Phone number"
                  name="phoneNumber"
                  placeholder="(123) 456-7890"
                  hideValidation
                  hideLabel
                />
              </div>
            </div>

            <Button fullWidth submit disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send"}
            </Button>
          </SimpleFormProvider>
        ) : (
          <div className="text-center">
            <Button onClick={onClose} fullWidth>
              Close
            </Button>
          </div>
        )}

        {/* Simple loading overlay */}
        {isSubmitting && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-color border-t-transparent"></div>
              <Text className="text-secondary" variant="body3">
                Sending hint...
              </Text>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

const shareGiftCard = ppost("/gift-cards/share");

const shareGiftCardSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneNumber: z.string().min(10, "Phone number must be at least 10 digits"),
});

type ShareGiftCardData = z.infer<typeof shareGiftCardSchema>;
